import React, { useEffect, useState, useRef, useMemo } from "react";

/**
 * MosaicReveal - 4x4 = 16 tiles progressively revealing the album artwork.
 * Uses requestAnimationFrame + timestamp diff for reliable timing on mobile
 * Safari (setInterval is throttled/paused in various iOS states).
 */
export const MosaicReveal = ({
  artwork,
  order = [],
  frozen = false,
  intervalMs = 500,
  size = "min(78vw, 55vh, 480px)",
}) => {
  const [revealed, setRevealed] = useState([]);
  const frozenRef = useRef(frozen);
  useEffect(() => { frozenRef.current = frozen; }, [frozen]);

  const orderKey = useMemo(() => (order && order.length === 16 ? order.join(",") : "default"), [order]);
  const ord = useMemo(() => {
    if (order && order.length === 16) return order.slice();
    return Array.from({ length: 16 }, (_, i) => i);
  }, [orderKey]);

  useEffect(() => {
    setRevealed([]);
    if (!artwork) return;
    let rafId = null;
    let idx = 0;
    let lastReveal = null;
    const tick = (now) => {
      if (lastReveal === null) lastReveal = now;
      if (!frozenRef.current && idx < 16 && now - lastReveal >= intervalMs) {
        lastReveal = now;
        const nextTile = ord[idx];
        idx += 1;
        setRevealed((prev) => (prev.includes(nextTile) ? prev : [...prev, nextTile]));
      }
      if (idx < 16) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => { if (rafId !== null) cancelAnimationFrame(rafId); };
  }, [artwork, orderKey, intervalMs, ord]);

  const revealedSet = useMemo(() => new Set(revealed), [revealed]);

  const tileStyle = (i) => ({
    opacity: revealedSet.has(i) ? 0 : 1,
    backgroundColor: "#1e1b4b",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
    // iOS: hint compositor + force GPU compositing so opacity transitions play smoothly
    willChange: "opacity",
    transform: "translateZ(0)",
    WebkitBackfaceVisibility: "hidden",
    backfaceVisibility: "hidden",
    transition: "opacity 260ms ease-out",
  });

  return (
    <div
      data-testid="mosaic-reveal"
      className="relative rounded-2xl overflow-hidden border-4 border-navy shadow-[0_18px_40px_rgba(0,0,0,0.45),0_8px_16px_rgba(0,0,0,0.25)]"
      style={{
        width: size,
        height: size,
        backgroundColor: "#1e1b4b",
        WebkitTransform: "translateZ(0)",
        transform: "translateZ(0)",
      }}
    >
      {artwork ? (
        <img
          src={artwork}
          alt="Album mosaic"
          className="absolute inset-0 w-full h-full object-cover select-none"
          draggable={false}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ WebkitTransform: "translateZ(0)" }}
        />
      ) : null}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
        {Array.from({ length: 16 }).map((_, i) => (
          <div
            key={i}
            data-testid={`mosaic-tile-${i}`}
            data-revealed={revealedSet.has(i) ? "true" : "false"}
            style={tileStyle(i)}
          />
        ))}
      </div>
      {frozen && (
        <div className="absolute top-2 right-2 bg-coral text-white font-bebas uppercase tracking-widest text-sm px-3 py-1 rounded-full border-2 border-white shadow-lg">
          🔒 Bevroren
        </div>
      )}
      <div
        data-testid="mosaic-count"
        className="absolute bottom-2 left-2 bg-navy/80 text-white font-bebas uppercase tracking-widest text-xs px-2.5 py-1 rounded-full border-2 border-white/70"
      >
        {revealed.length}/16
      </div>
    </div>
  );
};

export default MosaicReveal;
